import { observable, runInAction } from "mobx"

import { Model } from "@/lib/Model.ts"
import { toProfileUsername } from "@/lib/std.ts"
import { InviteTeammatesDialogModel } from "@/routes/auth/InviteTeammatesDialogModel.ts"
import { SlackNotificationsModel } from "@/routes/auth/SlackNotificationsModel.ts"
import { FigmaIntegrationTabModel } from "@/routes/user/FigmaIntegrationTabModel.ts"
import { GitHubIntegrationTabModel } from "@/routes/user/GitHubIntegrationTabModel.ts"
import { SshKeysTabModel } from "@/routes/user/SshKeysTabModel.ts"
import { isTabId, TabId } from "@/routes/user/UserSettingsTabId.ts"

import type { CPrincipal, TD_MemberProfile } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { Queries } from "@/lib/queries.ts"
import type { SettingsTabId } from "@/routes/user/UserSettingsTabId.ts"
import type { IObservableValue } from "mobx"
import type { MouseEvent } from "react"


export class UserSettingsDialogModel extends Model {
  private readonly modelsCache = new Map<SettingsTabId, Model>()

  readonly currentTab: IObservableValue<SettingsTabId>

  constructor(
    initialTab: string | undefined,
    private readonly deps: TabConstructorDeps
  ) {
    super("UserSettingsDialogModel")

    let tab: SettingsTabId = TabId.Profile
    if (initialTab && isTabId(initialTab)) {
      tab = initialTab
    }

    this.currentTab = observable.box(tab)
  }

  setTab = (tab: SettingsTabId) => {
    runInAction(() => this.currentTab.set(tab))
  }

  onTabClick = (tab: SettingsTabId) => {
    return (event: MouseEvent) => {
      event.preventDefault()
      this.setTab(tab)
    }
  }

  get profile() {
    return this.deps.profile
  }

  get sshModel() {
    const profile = this.deps.profile
    if (!profile) return null

    return this.getOrCreateTabModel<SshKeysTabModel>(TabId.SshKeys, () =>
      new SshKeysTabModel(this.deps.context, this.deps.queries, toProfileUsername(profile.username)))
  }

  get inviteModel() {
    return this.getOrCreateTabModel<InviteTeammatesDialogModel>(TabId.Teammates, () =>
      new InviteTeammatesDialogModel(this.deps.context, this.deps.queries, this.deps.principal)
    )
  }

  get figmaModel() {
    return this.getOrCreateTabModel<FigmaIntegrationTabModel>(TabId.Figma, () =>
      new FigmaIntegrationTabModel({
        context: this.deps.context,
        queries: this.deps.queries,
      })
    )
  }

  get slackModel() {
    return this.getOrCreateTabModel<SlackNotificationsModel>(TabId.Slack, () =>
      new SlackNotificationsModel(this.deps.queries)
    )
  }

  get gitHubModel() {
    return this.getOrCreateTabModel<GitHubIntegrationTabModel>(TabId.GitHub, () =>
      new GitHubIntegrationTabModel({
        context: this.deps.context,
        queries: this.deps.queries,
      })
    )
  }

  private getOrCreateTabModel = <T extends Model>(tabId: SettingsTabId, create: () => T): T => {
    if (!this.modelsCache.has(tabId)) {
      this.modelsCache.set(tabId, create())
    }

    return this.modelsCache.get(tabId) as T
  }

  terminate() {
    super.terminate()

    for (const model of this.modelsCache.values()) {
      model.terminate()
    }
  }
}

type TabConstructorDeps = {
  context: OrgContext
  queries: Queries
  profile: TD_MemberProfile | null
  principal: CPrincipal
}